import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars3Icon, BellIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { notificationsApi, type AppNotification } from '@/services/api';
import SelfClockButton from './SelfClockButton';

interface HeaderProps {
  title: string;
  subtitle?: string;
  onMenuClick?: () => void;
  actions?: React.ReactNode;
  /** Render the notification bell. Only the global header (DashboardLayout)
   *  sets this; per-page headers must not, to avoid a duplicate bell. */
  showBell?: boolean;
}

const LEVEL_DOT: Record<string, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

const CHANNEL_LABEL: Record<string, string> = { email: 'Email', telegram: 'Telegram', webpublish: 'Web', inapp: 'App' };
const DELIVERY_WORD: Record<string, string> = { sent: 'enviado', pending: 'pendiente', failed: 'fallido' };

/** Aggregate a notification's per-channel deliveries into one colored status. */
function deliveryStatus(deliveries?: AppNotification['deliveries']): { color: string; label: string } | null {
  if (!deliveries || deliveries.length === 0) return null;
  const detail = deliveries
    .map((d) => `${CHANNEL_LABEL[d.channel] || d.channel}: ${DELIVERY_WORD[d.status] || d.status}${d.lastError ? ` (${d.lastError})` : ''}`)
    .join(' · ');
  if (deliveries.some((d) => d.status === 'failed')) return { color: 'bg-red-500', label: detail };
  if (deliveries.some((d) => d.status === 'pending')) return { color: 'bg-amber-500', label: detail };
  return { color: 'bg-green-500', label: detail };
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

function NotificationBell() {
  const navigate = useNavigate();
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const r = await notificationsApi.unreadCount();
      setCount(r.data?.count || 0);
    } catch {
      /* silent */
    }
  }, []);

  // Poll the unread count (cheap) while mounted + when the org changes.
  useEffect(() => {
    if (!selectedOrgId) { setCount(0); return; }
    void refreshCount();
    const t = setInterval(refreshCount, 60000);
    return () => clearInterval(t);
  }, [selectedOrgId, refreshCount]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const r = await notificationsApi.list(30);
        setItems(r.data?.data || []);
      } catch {
        /* silent */
      }
    }
  };

  const openItem = async (n: AppNotification) => {
    if (n.status === 'pending') {
      try { await notificationsApi.markRead(n._id); } catch { /* silent */ }
      setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, status: 'read' } : x)));
      void refreshCount();
    }
    if (n.url) { setOpen(false); navigate(n.url); }
  };

  const dismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try { await notificationsApi.dismiss(id); } catch { /* silent */ }
    setItems((prev) => prev.filter((x) => x._id !== id));
    void refreshCount();
  };

  const markAll = async () => {
    try { await notificationsApi.markAllRead(); } catch { /* silent */ }
    setItems((prev) => prev.map((x) => ({ ...x, status: 'read' as const })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="p-2 text-gray-500 hover:text-gray-700 relative" aria-label="Notificaciones">
        <BellIcon className="w-6 h-6" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-xl shadow-xl border border-gray-200 z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notificaciones</span>
            <button onClick={markAll} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
              <CheckIcon className="w-3.5 h-3.5" /> Marcar todas
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">Sin notificaciones</p>
            ) : (
              items.map((n) => (
                <button
                  key={n._id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left flex gap-2 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${n.status === 'pending' ? 'bg-vida-50/40' : ''}`}
                >
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${LEVEL_DOT[n.level] || 'bg-gray-400'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">{n.title}</span>
                    {n.body && <span className="block text-xs text-gray-500 line-clamp-2">{n.body}</span>}
                    <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(n.createdAt)}</span>
                  </span>
                  {(() => {
                    const ds = deliveryStatus(n.deliveries);
                    return ds ? <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${ds.color}`} title={`Envío — ${ds.label}`} /> : null;
                  })()}
                  <span onClick={(e) => dismiss(e, n._id)} className="p-1 text-gray-300 hover:text-red-500 shrink-0" title="Descartar">
                    <XMarkIcon className="w-4 h-4" />
                  </span>
                </button>
              ))
            )}
          </div>
          <div className="flex items-center justify-center gap-3 px-4 py-2 border-t border-gray-100 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Enviado</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Pendiente</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Fallido</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ title, subtitle, onMenuClick, actions, showBell }: HeaderProps) {
  const { user } = useAuthStore();

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="p-2 -ml-2 text-gray-500 hover:text-gray-700 lg:hidden"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {actions}

          {showBell && <SelfClockButton />}

          {showBell && <NotificationBell />}

          {/* User avatar (mobile) */}
          <div className="lg:hidden">
            <div className="w-8 h-8 bg-vida-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
